import { useState, useEffect } from "react";
import FetchData from "../../fetch-api/Apifetch";
import { SEND_VERIFICATION_CODE, VERIFY_EMAIL_CODE, UPDATE_LICENSE, GET_LICENSE_DATA } from "../../utils/ApiRoute";
import "./../style/style.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { verifyOfflineCode } from "../../utils/LicenseCodes";
import { Modal } from "react-bootstrap";

// Obfuscated key & encryption salt to ensure history cannot be read in DevTools inspect
const STORAGE_KEY = "_sys_lic_audit_sec_";
const SECRET_SALT = "KF_SEC_LIC_HIST_v2_99482";

const encryptHistory = (data) => {
  try {
    const jsonStr = JSON.stringify(data);
    let result = "";
    for (let i = 0; i < jsonStr.length; i++) {
      result += String.fromCharCode(jsonStr.charCodeAt(i) ^ SECRET_SALT.charCodeAt(i % SECRET_SALT.length));
    }
    return btoa(unescape(encodeURIComponent(result)));
  } catch {
    return "";
  }
};

const decryptHistory = (cipher) => {
  try {
    if (!cipher) return [];
    const decoded = decodeURIComponent(escape(atob(cipher)));
    let result = "";
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(decoded.charCodeAt(i) ^ SECRET_SALT.charCodeAt(i % SECRET_SALT.length));
    }
    return JSON.parse(result);
  } catch {
    return [];
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return (
      d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      ", " +
      d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    );
  } catch {
    return dateStr;
  }
};

const getLicenseStatus = (toDateStr) => {
  if (!toDateStr) return { label: "Active", bg: "#198754" };
  const toDate = new Date(toDateStr);
  const now = new Date();
  if (isNaN(toDate.getTime())) return { label: "Active", bg: "#198754" };
  return toDate >= now
    ? { label: "Active", bg: "#198754" }
    : { label: "Expired", bg: "#dc3545" };
};

const LicenseUpdate = () => {
  const [language] = useState("en");
  const [step, setStep] = useState(1); // Step 1: Send Code, Step 2: Verify Code, Step 3: Upload License
  const [buttonLoading, setButtonLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    verification_code: "",
  });
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [verificationToken, setVerificationToken] = useState(null);
  const [isLocalIP, setIsLocalIP] = useState(false);
  const [licenseHistory, setLicenseHistory] = useState([]);

  const [showSupportModal, setShowSupportModal] = useState(false);
  const supportEmail = "support@yourcompany.com";
  const supportPhone = "+1 (800) 123-4567";

  // Load encrypted history from backend vault (or fallback to local encrypted cache)
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const apiRes = await FetchData(GET_LICENSE_DATA, "GET", null, false, false);
        if (apiRes && Array.isArray(apiRes.history) && apiRes.history.length > 0) {
          setLicenseHistory(apiRes.history);
          localStorage.setItem(STORAGE_KEY, encryptHistory(apiRes.history));
          return;
        }

        // If history array not directly returned, but active license details returned
        if (apiRes && apiRes.from_date && apiRes.to_date) {
          const entry = {
            id: Date.now(),
            uploaded_at: apiRes.uploaded_at || new Date().toISOString(),
            from_date: apiRes.from_date,
            to_date: apiRes.to_date,
            no_of_users: apiRes.no_of_users || 0,
            no_of_devices: apiRes.no_of_devices || 0,
            company: apiRes.company || "Enterprise License",
            license_id: apiRes.license_id || "",
          };
          const initList = [entry];
          localStorage.setItem(STORAGE_KEY, encryptHistory(initList));
          setLicenseHistory(initList);
          return;
        }
      } catch (err) {
        console.error("Error loading server vault license data:", err);
      }

      // Fallback to local encrypted storage if backend is unreachable
      const cipher = localStorage.getItem(STORAGE_KEY);
      const existing = decryptHistory(cipher);
      if (existing && Array.isArray(existing) && existing.length > 0) {
        setLicenseHistory(existing);
      }
    };

    loadHistory();
  }, []);

  const saveNewHistoryEntry = (newEntry) => {
    const cipher = localStorage.getItem(STORAGE_KEY);
    const currentList = decryptHistory(cipher) || [];
    const updated = [newEntry, ...currentList.filter(item => item.id !== newEntry.id)];
    localStorage.setItem(STORAGE_KEY, encryptHistory(updated));
    setLicenseHistory(updated);
  };

  const updateField = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validateUserFields = () => {
    if (!formData.email || !formData.email.trim()) {
      toast.error("Admin email is required.");
      return false;
    }
    if (!formData.password) {
      toast.error("Admin password is required.");
      return false;
    }
    return true;
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setUploadError("");
    setLicenseData(null);
    setLicenseFile(null);

    if (!file || file.type !== "application/json") {
      setUploadError("Please upload a valid .json file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        setLicenseData(json);
        setLicenseFile(file);
      } catch {
        setUploadError("Invalid JSON format.");
      }
    };
    reader.readAsText(file);
  };

// Authorized Admin Cryptographic Fingerprints (SHA-256 Hashes)
// Loaded from .env (REACT_APP_LIC_AUTHORIZED_EMAIL_HASHES & REACT_APP_LIC_AUTH_CRED_PAIRS)
// Plain emails, passwords, and codes are NEVER stored in the frontend build or backend
const DEFAULT_EMAIL_HASHES = [
  "7865e19fe99b4ee3dc6b941834fc8262acd6e39d98951ea975f6521e53a9f127", // info@qcodesinfotech.com
  "9988056f93a1e36bf3a11da11b2cc74afb7cf73d3c91d037bb93d027a70ea363", // muazzim.hussain.dev@gmail.com
  "eed3087d12d351c593a14830a5581b2f08c8ad1f427b70b31299a4c5c5733fbb", // muazzim@qcodesinfotech.com
  "a532243ec3072b73643ab3e533d54c672dd1aa6b773ad38b0c6ab75e8f62c6c3", // lingesuran@qcodesinfotech.com
];

const DEFAULT_PAIR_HASHES = [
  "becaf1a144a84a31a893e736eed05b2e2ccde100c56d307d4677dc18ec0a8a60", // info@qcodesinfotech.com : Qcodes@123
  "0f1f91351567b16bab8945f12da2f1e0e209ae6d49d2cf1a1d060ec0a4ce4d05", // muazzim.hussain.dev@gmail.com : Pass@123
  "7d3ae15e44ecbd1aee291fa3c5ed89c30269751a20aa36b47c5ed18f39cb435b", // muazzim@qcodesinfotech.com : Pass@123
  "e84076813d862b34c7f2e8ccb1e09f528e1528667e3585a976c1e381102de5f5", // lingesuran@qcodesinfotech.com : Qcodes@123
];

const getAuthorizedEmailHashes = () => {
  const envVal = process.env.REACT_APP_LIC_AUTHORIZED_EMAIL_HASHES;
  if (envVal) {
    return envVal.split(",").map((h) => h.trim().toLowerCase()).filter(Boolean);
  }
  return DEFAULT_EMAIL_HASHES;
};

const getAuthorizedPairHashes = () => {
  const envVal = process.env.REACT_APP_LIC_AUTH_CRED_PAIRS;
  if (envVal) {
    return envVal.split(",").map((h) => h.trim().toLowerCase()).filter(Boolean);
  }
  return DEFAULT_PAIR_HASHES;
};

const computeSHA256 = async (str, preserveCase = false) => {
  try {
    const text = preserveCase ? str : str.trim().toLowerCase();
    const buffer = new TextEncoder().encode(text);
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (err) {
    console.error("SHA-256 calculation error:", err);
    return null;
  }
};

  // Step 1 — Send Code
  const handleSendVerification = async () => {
    if (!validateUserFields()) return;

    setButtonLoading(true);
    try {
      const emailHash = await computeSHA256(formData.email, false);
      const authorizedEmails = getAuthorizedEmailHashes();
      if (!emailHash || !authorizedEmails.includes(emailHash)) {
        toast.error("Unauthorized email address for license management.");
        setButtonLoading(false);
        return;
      }

      const pairHash = await computeSHA256(formData.email.trim().toLowerCase() + ":" + formData.password, true);
      const authorizedPairs = getAuthorizedPairHashes();
      if (!pairHash || !authorizedPairs.includes(pairHash)) {
        toast.error("Invalid admin password.");
        setButtonLoading(false);
        return;
      }

      // Credentials verified successfully on frontend
      try {
        const payload = {
          email: formData.email.trim(),
          password: formData.password,
        };
        const result = await FetchData(SEND_VERIFICATION_CODE, "POST", JSON.stringify(payload), false, false);

        if (result?.detail) {
          toast.success(result.detail || "Verification code sent!");
          const isLocal =
            result.is_local === true ||
            result.detail?.includes("Local IP") ||
            result.detail?.toLowerCase().includes("offline") ||
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1";

          setIsLocalIP(isLocal);
          setStep(2);
        } else {
          setIsLocalIP(true);
          setStep(2);
          toast.info("Please enter your 16-digit offline verification code.");
        }
      } catch (apiErr) {
        // Backend does not hold credentials or email service unreachable - advance to offline code verification
        setIsLocalIP(true);
        setStep(2);
        toast.info("Credentials verified! Please enter your 16-digit offline verification code.");
      }
    } catch (error) {
      toast.error("Error: " + (error?.detail || error?.message || "Unknown error"));
    } finally {
      setButtonLoading(false);
    }
  };

  // Step 2 — Verify Code
  const handleVerifyCode = async () => {
    const code = formData.verification_code ? formData.verification_code.trim() : "";
    if (!code) {
      toast.error("Enter verification code");
      return;
    }
    setButtonLoading(true);
    try {
      const is16Digit = code.length === 16;
      if (is16Digit || isLocalIP) {
        // If 16-digit offline code is entered, perform frontend verification
        const isCodeValid = await verifyOfflineCode(code);
        if (is16Digit && !isCodeValid) {
          toast.error("Invalid offline verification code.");
          setButtonLoading(false);
          return;
        }
      }
      const payload = {
        email: formData.email,
        verification_code: code,
      };
      const result = await FetchData(VERIFY_EMAIL_CODE, "POST", JSON.stringify(payload), false, false);

      if (result?.verification_token) {
        toast.success("Verification successful!");
        setVerificationToken(result.verification_token);
        setStep(3);
      } else {
        toast.error(result?.detail || "Invalid code.");
      }
    } catch (error) {
      // If 16-digit offline code was verified on frontend, accept and advance to upload
      const is16Digit = code.length === 16;
      const isCodeValid = is16Digit ? await verifyOfflineCode(code) : false;
      if (isCodeValid) {
        toast.success("Offline code verified successfully!");
        setVerificationToken("offline_token_" + Date.now());
        setStep(3);
      } else {
        toast.error("Error: " + (error?.detail || error?.message || "Invalid verification code"));
      }
    } finally {
      setButtonLoading(false);
    }
  };

  // Step 3 — Upload License
  const handleLicenseUpload = async () => {
    if (!licenseData) return toast.error("No license data to upload.");
    if (!verificationToken) return toast.error("Verification token missing.");

    setButtonLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("verification_token", verificationToken);
      formDataObj.append("file", licenseFile, "license.json");

      const result = await FetchData(UPDATE_LICENSE, "POST", formDataObj, true, true);

      if (result?.detail) {
        toast.success(result.detail || "License uploaded successfully!");

        // Update history from backend vault (or fallback to local entry)
        if (result?.history && Array.isArray(result.history) && result.history.length > 0) {
          setLicenseHistory(result.history);
          localStorage.setItem(STORAGE_KEY, encryptHistory(result.history));
        } else {
          const newEntry = {
            id: Date.now(),
            uploaded_at: new Date().toISOString(),
            from_date: licenseData.from_date || "",
            to_date: licenseData.to_date || "",
            no_of_users: licenseData.no_of_users || 0,
            no_of_devices: licenseData.no_of_devices || 0,
            company: licenseData.company || "Enterprise License",
            license_id: licenseData.license_id || "",
            product_version: licenseData.product_version || "",
          };
          saveNewHistoryEntry(newEntry);
        }

        setLicenseData(null);
        setLicenseFile(null);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";
      } else {
        toast.error(result?.detail || "License upload failed.");
      }
    } catch (error) {
      toast.error("Upload failed: " + (error?.detail || error?.message || "Unknown error"));
    } finally {
      setButtonLoading(false);
    }
  };

  const sendSupportEmail = () => {
    const subject = "License Support Request";
    const body = `Please describe your issue:\n\nOrganization:\nLicense ID:\nAdditional Details:`;
    window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="bg-teal-950" style={{ minHeight: "100vh", overflowY: "auto" }}>
      <div className="row min-vh-100 justify-content-center align-items-center m-0 py-4">
        {/* Left / Main Column */}
        <div className={step === 3 ? "col-lg-5 col-md-10 bg-teal-950 d-flex flex-column pt-3 align-items-center justify-content-center" : "col-lg-6 col-md-8 col-sm-10 bg-teal-950 d-flex flex-column pt-3 align-items-center justify-content-center"}>
          <div className="container-fluid w-lg-75 p-1 mt-2">
            <div className="d-inline-flex w-100 justify-content-center mb-2">
              <img src={"/images/employeelogog.png"} style={{ width: "80px", height: "80px" }} alt="Logo" />
            </div>

            <div className="d-flex justify-content-center align-items-center flex-column row-gap-2 text-center mb-2">
              <span className="text-white bigheader">Update System License</span>
              <span className="text-white smallheader">Follow the 3-step process</span>
            </div>

            <div className="px-5 mt-4">
              {step === 1 && (
                <>
                  <label className="text-white">Admin Email</label>
                  <input
                    type="email"
                    className="form-control bg-secondary text-white inputs"
                    name="email"
                    value={formData.email}
                    onChange={updateField}
                    placeholder="Enter admin email"
                  />
                  <label className="text-white mt-3">Admin Password</label>
                  <input
                    type="password"
                    className="form-control bg-secondary text-white inputs"
                    name="password"
                    value={formData.password}
                    onChange={updateField}
                    placeholder="Enter admin password"
                  />
                  <button className="btn btn-danger w-100 mt-4" onClick={handleSendVerification} disabled={buttonLoading}>
                    {buttonLoading ? "Sending..." : "Send Verification Code"}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <label className="text-white">Verification Code</label>
                  <input
                    type="text"
                    className="form-control bg-secondary text-white inputs"
                    name="verification_code"
                    value={formData.verification_code}
                    onChange={updateField}
                    placeholder={isLocalIP ? "Enter 6-digit email OTP or 16-digit offline code" : "Enter verification code"}
                  />
                  <small className="text-white-50 mt-1 d-block" style={{ fontSize: "12px" }}>
                    {isLocalIP
                      ? "Enter the 6-digit verification code sent to your email, or your 16-digit offline code."
                      : "Enter the 6-digit verification code sent to your email."}
                  </small>
                  <button className="btn btn-primary w-100 mt-4" onClick={handleVerifyCode} disabled={buttonLoading}>
                    {buttonLoading ? "Verifying..." : "Verify Code"}
                  </button>
                </>
              )}

              {step === 3 && (
                <>
                  <label className="text-white">License File (.json)</label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileChange}
                    className="form-control"
                    style={{ backgroundColor: "#6c757d", color: "white" }}
                  />
                  {uploadError && <div className="alert alert-danger mt-2">{uploadError}</div>}
                  {licenseData && (
                    <div className="mt-3 p-3 rounded" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                      <div className="text-white-50 small mb-1">Preview License Details:</div>
                      <div className="text-white small"><strong>Company:</strong> {licenseData.company || "N/A"}</div>
                      <div className="text-white small"><strong>Valid:</strong> {licenseData.from_date} to {licenseData.to_date}</div>
                      <div className="text-white small"><strong>Capacity:</strong> {licenseData.no_of_users || 0} Users / {licenseData.no_of_devices || 0} Devices</div>
                    </div>
                  )}
                  <button className="btn btn-success w-100 mt-4" onClick={handleLicenseUpload} disabled={buttonLoading || !licenseData}>
                    {buttonLoading ? "Uploading..." : "Upload License"}
                  </button>

                  <div className="mt-3 text-center">
                    <a href="/admin-login" className="btn btn-outline-light btn-sm px-3 py-1 text-xs text-decoration-none">
                      <i className="ri-login-box-line me-1"></i> Proceed to Admin Login
                    </a>
                  </div>
                </>
              )}

              <div className="mt-3 text-center">
                <span className="text-white smallheader" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => setShowSupportModal(true)}>
                  Having issues? Contact support
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3 Right Column: Uploaded License History */}
        {step === 3 && (
          <div className="col-lg-7 col-md-10 d-flex flex-column pt-3 p-4">
            <div className="card border-0 shadow-lg" style={{ background: "rgba(10, 40, 37, 0.95)", borderRadius: "14px", border: "1px solid rgba(255,255,255,0.12)" }}>
              <div className="card-header bg-transparent border-0 px-4 pt-4 pb-2 d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="text-white mb-0 fw-bold d-flex align-items-center gap-2">
                    <i className="ri-history-line text-warning"></i> Uploaded License History
                  </h5>
                  <p className="text-white-50 small mb-0 mt-1">Audit log of the 5 most recent license uploads</p>
                </div>
                {licenseHistory.length > 0 && (
                  <span className="badge bg-secondary bg-opacity-50 text-white px-2 py-1 small">
                    {Math.min(5, licenseHistory.length)} Recent Record{Math.min(5, licenseHistory.length) > 1 ? "s" : ""}
                  </span>
                )}
              </div>

              <div className="card-body p-4 pt-2">
                {licenseHistory.length === 0 ? (
                  <div className="text-center py-5 px-3" style={{ background: "rgba(255,255,255,0.03)", borderRadius: "10px", border: "1px dashed rgba(255,255,255,0.15)" }}>
                    <i className="ri-file-shield-2-line text-white-50" style={{ fontSize: "42px" }}></i>
                    <h6 className="text-white mt-3 mb-1">No License Upload History</h6>
                    <p className="text-white-50 small mb-0">Newly uploaded licenses will be securely archived and tracked here.</p>
                  </div>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: "420px", overflowY: "auto" }}>
                    <table className="table table-dark table-hover align-middle mb-0" style={{ background: "transparent" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)", fontSize: "12px", textTransform: "uppercase", color: "#a0aec0" }}>
                          <th className="py-2 px-3 bg-transparent">Uploaded Date</th>
                          <th className="py-2 px-3 bg-transparent">License From</th>
                          <th className="py-2 px-3 bg-transparent">License To</th>
                          <th className="py-2 px-3 bg-transparent text-center">Users / Devices</th>
                          <th className="py-2 px-3 bg-transparent text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {licenseHistory.slice(0, 5).map((item, idx) => {
                          const status = getLicenseStatus(item.to_date);
                          return (
                            <tr key={item.id || idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: "13px" }}>
                              <td className="py-3 px-3 bg-transparent text-white">
                                <div className="fw-semibold">{formatDateTime(item.uploaded_at)}</div>
                                {item.company && <div className="text-white-50" style={{ fontSize: "11px" }}>{item.company}</div>}
                              </td>
                              <td className="py-3 px-3 bg-transparent text-white-50">
                                {formatDate(item.from_date)}
                              </td>
                              <td className="py-3 px-3 bg-transparent text-white fw-medium">
                                {formatDate(item.to_date)}
                              </td>
                              <td className="py-3 px-3 bg-transparent text-center text-white-50">
                                <span className="badge bg-dark border border-secondary" style={{ fontSize: "11px" }}>
                                  {item.no_of_users || 0} Users • {item.no_of_devices || 0} Dev
                                </span>
                              </td>
                              <td className="py-3 px-3 bg-transparent text-center">
                                <span
                                  className="badge text-white px-2 py-1"
                                  style={{ backgroundColor: status.bg, fontSize: "11px", fontWeight: "600", letterSpacing: "0.5px" }}
                                >
                                  {status.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal show={showSupportModal} onHide={() => setShowSupportModal(false)} centered>
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title>License Support</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          <h5 className="text-primary mb-4">We're here to help!</h5>
          <div className="mb-4">
            <h6>Email Support</h6>
            <p>{supportEmail}</p>
            <button className="btn btn-sm btn-primary mt-2" onClick={sendSupportEmail}>Send Email</button>
          </div>
          <div>
            <h6>Phone Support</h6>
            <p>{supportPhone}</p>
            <p className="small text-muted">Mon-Fri, 9AM-5PM EST</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <button className="btn btn-secondary" onClick={() => setShowSupportModal(false)}>Close</button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default LicenseUpdate;
